import React from 'react'

// Constants
import { routes } from '@sketch/constants'

// Utils
import { getShareThumbnail } from 'modules/shares/utils'
import { getGridColumnSize } from '../../utils/grid'

// Components
import { Column, Grid } from 'components/Grid'
import { ProfileDocument } from '../../components/ProfileDocument'
import { useGetPublications } from '../../hooks/useGetPublications'
import { Skeleton, ErrorMessage } from '@sketch/components'

// Styles
import { EmptyState } from './WorkspaceProfilePublications.styles'

// Types
import type {
  WorkspaceProfileResultLoading,
  WorkspaceProfileResultSuccess,
} from 'modules/workspaceProfile/hooks/useGetWorkspaceProfile'
import type { ShareForDocumentItem } from 'modules/shares/components/DocumentItem'

// Styles
interface WorkspaceProfileLayoutProps {
  workspaceId: string
  isProfileEditRoute: boolean
  profileResult: WorkspaceProfileResultSuccess | WorkspaceProfileResultLoading
}

const DocumentGridSkeleton = () => (
  <Grid gutter={{ xl: 33, sm: 28, xs: 22 }}>
    {[0, 1, 2, 3].map(placeholder => {
      const columnSize = getGridColumnSize(placeholder)

      return (
        <Column key={`placeholder-skeleton-${placeholder}`} lg={columnSize}>
          <Skeleton width="100%" height="390px" />
        </Column>
      )
    })}
  </Grid>
)

export const WorkspaceProfilePublications = (
  props: WorkspaceProfileLayoutProps
) => {
  const { workspaceId, isProfileEditRoute, profileResult } = props
  const { profile } = profileResult

  const {
    publications,
    placeholders,
    isEmpty,
    error,
    loading,
  } = useGetPublications(workspaceId)

  if (loading) {
    return <DocumentGridSkeleton />
  }

  if (isEmpty) {
    return (
      <EmptyState
        workspaceId={workspaceId}
        workspaceName={profile?.workspace.name!}
        editing={isProfileEditRoute}
      />
    )
  }

  if (error) {
    return <ErrorMessage.Generic />
  }

  const isSingleDocument = publications.length === 1

  return (
    <Grid gutter={{ xl: 33, sm: 28, xs: 22 }}>
      {publications.map(({ share }, index) => {
        const thumbnail = getShareThumbnail(
          (share as unknown) as ShareForDocumentItem
        )

        // When a single document is published we want it centered but keep its ratio.
        // Column layout for single file: [2] [8] [2]
        if (isSingleDocument) {
          return (
            <>
              <Column key="spacer-l" lg={2}></Column>
              <Column key={`profile-document-${share.identifier}`} lg={8}>
                <ProfileDocument
                  thumbnail={thumbnail!}
                  name={share.name}
                  url={routes.SHARE_VIEW.create({
                    shareID: share.identifier,
                  })}
                />
              </Column>
              <Column key="spacer-r" lg={2}></Column>
            </>
          )
        }

        return (
          <Column
            key={`profile-document-${share.identifier}`}
            lg={getGridColumnSize(index)}
          >
            <ProfileDocument
              thumbnail={thumbnail!}
              name={share.name}
              url={routes.SHARE_VIEW.create({
                shareID: share.identifier,
              })}
            />
          </Column>
        )
      })}

      {placeholders.map((placeholder, placeholderIndex) => {
        const index = publications.length + placeholderIndex

        const columnSize = getGridColumnSize(index)

        return (
          <Column key={`placeholder-${index}-${columnSize}`} lg={columnSize}>
            {placeholder}
          </Column>
        )
      })}
    </Grid>
  )
}
